import { useEffect, useState } from "react";
import { ToastFeedback } from "Components/atoms/ToastFeedback";
import {
  getNotificationPreferences,
  NotificationPreferences,
  updateNotificationPreferences,
} from "shared/auth/auth";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";
import {
  NotificationOption,
  NotificationText,
  OptionList,
  Switch,
} from "./styles";
import { AccountLoader } from "./AccountLoader";

type Channel = "email" | "whatsapp";
type ChannelState = Record<Channel, boolean>;

const toBoolean = (value: boolean | number | string | undefined) =>
  value === true || value === 1 || value === "1";

const getChannelState = (
  preferences: NotificationPreferences,
): ChannelState => ({
  email: toBoolean(preferences.isEmailPromotionalNotifications),
  whatsapp: toBoolean(preferences.isWhatsAppPromotionalNotifications),
});

const getChannelPayload = (
  channel: Channel,
  enabled: boolean,
): NotificationPreferences =>
  channel === "email"
    ? {
        isEmailPromotionalNotifications: enabled,
      }
    : {
        isWhatsAppPromotionalNotifications: enabled,
      };

export function NotificationPreferencesForm() {
  const [channels, setChannels] = useState<ChannelState>({
    email: false,
    whatsapp: false,
  });
  const [loading, setLoading] = useState(true);
  const [savingChannel, setSavingChannel] = useState<Channel | null>(null);
  const [feedback, setFeedback] = useState<{
    message: string;
    isError?: boolean;
  } | null>(null);

  useEffect(() => {
    let active = true;

    getNotificationPreferences()
      .then((preferences) => {
        if (active) setChannels(getChannelState(preferences));
      })
      .catch((error) => {
        if (!active) return;
        setFeedback({
          message:
            error instanceof Error
              ? error.message
              : "Não foi possível carregar suas preferências.",
          isError: true,
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const toggleChannel = async (channel: Channel) => {
    const previousValue = channels[channel];
    const nextValue = !previousValue;

    setChannels((current) => ({ ...current, [channel]: nextValue }));
    setSavingChannel(channel);
    setFeedback(null);

    try {
      await updateNotificationPreferences(
        getChannelPayload(channel, nextValue),
      );
      sendGTMEvent("accountPreferenceUpdate", {
        platform: getPlatformType(),
        channel,
        action: nextValue ? "enable" : "disable",
        result: "success",
      });
      setFeedback({ message: "Preferência atualizada." });
    } catch (error) {
      sendGTMEvent("accountPreferenceUpdate", {
        platform: getPlatformType(),
        channel,
        action: nextValue ? "enable" : "disable",
        result: "error",
      });
      setChannels((current) => ({ ...current, [channel]: previousValue }));
      setFeedback({
        message:
          error instanceof Error
            ? error.message
            : "Não foi possível atualizar a preferência.",
        isError: true,
      });
    } finally {
      setSavingChannel(null);
    }
  };

  if (loading) {
    return <AccountLoader label="Carregando preferências..." />;
  }

  return (
    <>
      <ToastFeedback
        message={feedback?.message ?? ""}
        type={feedback?.isError ? "error" : "success"}
        isOpen={Boolean(feedback)}
        onClose={() => setFeedback(null)}
      />

      <OptionList>
        <NotificationOption>
          <NotificationText>
            <strong>Notificações por E-mail</strong>
            <span>Alertas de passagens, ofertas e conteúdos por e-mail.</span>
          </NotificationText>
          <Switch
            type="button"
            role="switch"
            aria-label="Notificações por e-mail"
            aria-checked={channels.email}
            $checked={channels.email}
            disabled={savingChannel !== null}
            onClick={() => void toggleChannel("email")}
          />
        </NotificationOption>

        <NotificationOption>
          <NotificationText>
            <strong>Notificações por WhatsApp</strong>
            <span>Alertas de passagens, ofertas e mensagens por WhatsApp.</span>
          </NotificationText>
          <Switch
            type="button"
            role="switch"
            aria-label="Notificações por WhatsApp"
            aria-checked={channels.whatsapp}
            $checked={channels.whatsapp}
            disabled={savingChannel !== null}
            onClick={() => void toggleChannel("whatsapp")}
          />
        </NotificationOption>
      </OptionList>
    </>
  );
}
